import {
  HStack,
  Text,
  Icon,
  Collapse,
  VStack,
  Box,
  useDisclosure,
  Spinner,
  Divider,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  FiChevronRight,
  FiChevronDown,
  FiCheck,
  FiClock,
} from "react-icons/fi";
import { ToolExecution } from "services/luna";
import { BlockRenderer } from "./blocks";

interface LunaThinkingStepsProps {
  sessionId: string;
  stepCount: number;
  toolExecutions?: ToolExecution[];
}

export const LunaThinkingSteps = ({
  sessionId,
  stepCount,
  toolExecutions,
}: LunaThinkingStepsProps) => {
  const { isOpen, onToggle, onOpen } = useDisclosure();
  const hasAutoOpened = useRef(false);
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());

  const toggleToolExpanded = (toolId: string) => {
    setExpandedTools((prev) => {
      const next = new Set(prev);
      if (next.has(toolId)) {
        next.delete(toolId);
      } else {
        next.add(toolId);
      }
      return next;
    });
  };

  // Separate running, pending confirmation, pending external, and completed tools
  const {
    runningTools,
    pendingConfirmationTools,
    pendingExternalTools,
    completedTools,
  } = useMemo(() => {
    const running =
      toolExecutions?.filter(
        (t) => t.status === "running" || t.status === "pending",
      ) || [];
    const pendingConfirmation =
      toolExecutions?.filter((t) => t.status === "pending_confirmation") || [];
    const pendingExternal =
      toolExecutions?.filter((t) => t.status === "pending_external") || [];
    const completed =
      toolExecutions?.filter(
        (t) => t.status === "completed" || t.status === "confirmed",
      ) || [];
    return {
      runningTools: running,
      pendingConfirmationTools: pendingConfirmation,
      pendingExternalTools: pendingExternal,
      completedTools: completed,
    };
  }, [toolExecutions]);

  const hasRunningTools = runningTools.length > 0;
  const hasPendingConfirmation = pendingConfirmationTools.length > 0;
  const hasPendingExternal = pendingExternalTools.length > 0;

  // Auto-open once when tools are running, pending confirmation, or pending external
  useEffect(() => {
    if (
      (hasRunningTools || hasPendingConfirmation || hasPendingExternal) &&
      !hasAutoOpened.current
    ) {
      hasAutoOpened.current = true;
      onOpen();
    }
  }, [hasRunningTools, hasPendingConfirmation, hasPendingExternal, onOpen]);

  // Build the header text
  const headerText = useMemo(() => {
    const parts: string[] = [];

    if (hasPendingConfirmation) {
      parts.push(
        `${pendingConfirmationTools.length} awaiting confirmation`,
      );
    }
    if (hasPendingExternal) {
      parts.push(
        `${pendingExternalTools.length} awaiting response`,
      );
    }
    if (hasRunningTools) {
      parts.push(`${runningTools.length} running`);
    }
    if (stepCount > 0) {
      parts.push(`${stepCount} completed`);
    }

    if (parts.length === 0) {
      return `${stepCount} step${stepCount !== 1 ? "s" : ""} completed`;
    }

    return parts.join(", ");
  }, [
    hasRunningTools,
    hasPendingConfirmation,
    hasPendingExternal,
    runningTools.length,
    pendingConfirmationTools.length,
    pendingExternalTools.length,
    stepCount,
  ]);

  return (
    <Box>
      <HStack
        as="button"
        onClick={onToggle}
        spacing="2xs"
        cursor="pointer"
        _hover={{ opacity: 0.8 }}
      >
        <Text fontSize="xs" color="text.body.tertiary" fontWeight="medium">
          {headerText}
        </Text>
        <Icon
          as={isOpen ? FiChevronDown : FiChevronRight}
          color="text.body.tertiary"
          boxSize={4}
        />
      </HStack>

      <Collapse in={isOpen} animateOpacity>
        <VStack align="stretch" spacing="sm" mt="sm">
          {/* Pending confirmation tools - render confirmation block with preview blocks */}
          {pendingConfirmationTools.map((execution, i) => {
            // Find the confirmation block from call_blocks
            const confirmationBlock = execution.call_blocks?.find(
              (block) => block.type === "confirmation",
            );
            // Get non-confirmation blocks for preview
            const previewBlocks = execution.call_blocks?.filter(
              (block) => block.type !== "confirmation",
            );

            if (!confirmationBlock) return null;

            return (
              <Box key={execution.id || `pending-confirm-${i}`} w="full">
                <BlockRenderer
                  block={confirmationBlock}
                  sessionId={sessionId}
                  executionStatus={execution.status}
                  previewBlocks={previewBlocks}
                />
              </Box>
            );
          })}

          {/* Pending external tools - render confirmation block to show what was sent */}
          {pendingExternalTools.map((execution, i) => {
            // Find the confirmation block from call_blocks
            const confirmationBlock = execution.call_blocks?.find(
              (block) => block.type === "confirmation",
            );
            // Get non-confirmation blocks for preview
            const previewBlocks = execution.call_blocks?.filter(
              (block) => block.type !== "confirmation",
            );

            // If there's a confirmation block, render it with pending_external status
            if (confirmationBlock) {
              return (
                <Box key={execution.id || `pending-external-${i}`} w="full">
                  <BlockRenderer
                    block={confirmationBlock}
                    sessionId={sessionId}
                    executionStatus={execution.status}
                    previewBlocks={previewBlocks}
                  />
                </Box>
              );
            }

            // Fallback: just show a simple waiting indicator
            const label =
              execution.tool_label || execution.tool_name || "Action";
            return (
              <HStack key={execution.id || `pending-external-${i}`} spacing="xs">
                <Icon as={FiClock} color="p.500" boxSize={3} />
                <Text fontSize="xs" color="text.body.secondary">
                  {label} - Waiting for result
                </Text>
              </HStack>
            );
          })}

          {/* Running tools with spinner */}
          {runningTools.map((execution, i) => {
            const label =
              execution.tool_label || execution.tool_name || "tool";
            return (
              <HStack key={execution.id || `running-${i}`} spacing="xs">
                <Spinner size="xs" color="luna.500" />
                <Text fontSize="xs" color="text.body.secondary">
                  {execution.confirmation_message || `Running ${label}...`}
                </Text>
              </HStack>
            );
          })}

          {/* Completed tools with checkmark - expandable */}
          {completedTools.map((execution, i) => {
            const toolId = execution.id || `completed-${i}`;
            const isToolExpanded = expandedTools.has(toolId);
            const label =
              execution.tool_label ||
              execution.tool_name ||
              `Tool execution ${i + 1}`;
            const hasBlocks =
              (execution.call_blocks && execution.call_blocks.length > 0) ||
              (execution.response_blocks && execution.response_blocks.length > 0);

            return (
              <Box key={toolId}>
                <HStack
                  spacing="xs"
                  cursor={hasBlocks ? "pointer" : "default"}
                  onClick={() => hasBlocks && toggleToolExpanded(toolId)}
                  _hover={hasBlocks ? { opacity: 0.8 } : {}}
                >
                  <Icon as={FiCheck} color="success.500" boxSize={3} flexShrink={0} />
                  <Text fontSize="xs" color="text.body.secondary">
                    {execution.completion_message || execution.confirmation_message || label}
                  </Text>
                  {hasBlocks && (
                    <Icon
                      as={isToolExpanded ? FiChevronDown : FiChevronRight}
                      color="text.body.tertiary"
                      boxSize={3}
                      flexShrink={0}
                    />
                  )}
                </HStack>

                {hasBlocks && (
                  <Collapse in={isToolExpanded} animateOpacity>
                    <Box
                      mt="sm"
                      ml="md"
                      pl="sm"
                      borderLeftWidth="2px"
                      borderLeftColor="border.default"
                    >
                      <VStack align="stretch" spacing="sm">
                        {/* Call blocks (input parameters) */}
                        {execution.call_blocks &&
                          execution.call_blocks.length > 0 &&
                          execution.call_blocks
                            .filter((block) => block.type !== "confirmation")
                            .map((block, blockIndex) => (
                              <BlockRenderer
                                key={`call-${blockIndex}`}
                                block={block}
                                sessionId={sessionId}
                              />
                            ))}

                        {/* Divider between call and response blocks */}
                        {execution.call_blocks &&
                          execution.call_blocks.filter((b) => b.type !== "confirmation").length > 0 &&
                          execution.response_blocks &&
                          execution.response_blocks.length > 0 && (
                            <Divider borderColor="border.default" />
                          )}

                        {/* Response blocks (output) */}
                        {execution.response_blocks &&
                          execution.response_blocks.length > 0 &&
                          execution.response_blocks.map((block, blockIndex) => (
                            <BlockRenderer
                              key={`response-${blockIndex}`}
                              block={block}
                              sessionId={sessionId}
                            />
                          ))}
                      </VStack>
                    </Box>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </VStack>
      </Collapse>
    </Box>
  );
};
